import { useTranslation } from "react-i18next";
import { FileText, TrendingUp, TrendingDown } from "lucide-react";

const NewsSentimentBar = ({ news }) => {
  const { t } = useTranslation();
  if (!news || !news.articles_analyzed) return null;

  // Normalize score from [-1, 1] → bullish/bearish % split
  const bullishPct = Math.round(((news.avg_score + 1) / 2) * 100);
  const bearishPct = 100 - bullishPct;

  return (
    <div className="space-y-2 bg-gray-50 dark:bg-slate-900/50 rounded-xl p-3 border border-gray-200 dark:border-white/10 backdrop-blur-md shadow-lg min-w-0">
      {/* Header -- this renders inside a narrow grid cell (as little as
          ~150px at the 5-column breakpoint), so both sides need to be able
          to shrink/wrap rather than force the card wider than its column. */}
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-700 dark:text-gray-300 min-w-0">
          <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <span className="truncate">{t('stocks.newsSentimentBar.articlesAnalyzed', { count: news.articles_analyzed })}</span>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
          {t('stocks.newsSentimentBar.avgScoreLabel')}{" "}
          <span
            className={`font-semibold ${
              news.avg_score >= 0
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-red-700 dark:text-red-400"
            }`}
          >
            {news.avg_score.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Sentiment Split Progress */}
      <div className="relative w-full bg-gray-200 dark:bg-gray-800 rounded-full h-3 overflow-hidden shadow-inner border border-gray-300 dark:border-white/10">
        <div className="absolute inset-0 flex">
          <div
            className="bg-gradient-to-r from-emerald-500/80 to-emerald-400/60 backdrop-blur-md transition-all"
            style={{ width: `${bullishPct}%` }}
          />
          <div
            className="bg-gradient-to-r from-rose-500/80 to-rose-400/60 backdrop-blur-md transition-all"
            style={{ width: `${bearishPct}%` }}
          />
        </div>
      </div>

      {/* Chips */}
      <div className="flex flex-wrap justify-between gap-1 mt-1">
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
          bg-emerald-50 text-emerald-700 border border-emerald-300 dark:bg-emerald-400/20 dark:text-emerald-300 dark:border-emerald-500/30 backdrop-blur-md shadow-sm whitespace-nowrap">
          <TrendingUp className="w-3 h-3 flex-shrink-0" />
          {t('stocks.newsSentimentBar.bullishPct', { pct: bullishPct })}
        </span>
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
          bg-rose-50 text-rose-700 border border-rose-300 dark:bg-rose-400/20 dark:text-rose-300 dark:border-rose-500/30 backdrop-blur-md shadow-sm whitespace-nowrap">
          <TrendingDown className="w-3 h-3 flex-shrink-0" />
          {t('stocks.newsSentimentBar.bearishPct', { pct: bearishPct })}
        </span>
      </div>

      {/* Extra Info -- was always rendered, even when neither source was
          available, showing two boxes of nothing but an em dash. Source
          attribution is genuinely optional data (not every article feed
          reports it), so this whole block only earns its space when there's
          at least one real value to show. */}
      {(news.top_bullish_source || news.top_bearish_source) && (
        <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {news.top_bullish_source && (
            <div className="p-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 min-w-0">
              <span className="block text-gray-500 truncate">{t('stocks.newsSentimentBar.mostPositiveSource')}</span>
              <span className="text-emerald-700 dark:text-emerald-300 font-semibold truncate block">
                {news.top_bullish_source}
              </span>
            </div>
          )}
          {news.top_bearish_source && (
            <div className="p-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 min-w-0">
              <span className="block text-gray-500 truncate">{t('stocks.newsSentimentBar.mostNegativeSource')}</span>
              <span className="text-rose-700 dark:text-rose-300 font-semibold truncate block">
                {news.top_bearish_source}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsSentimentBar;
